import styled from 'styled-components';
import { motion } from 'framer-motion';

// ==========================================
// Layout Components
// ==========================================

export const PageContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: #f1f5f9;
`;

export const Sidebar = styled.aside`
  width: 280px;
  background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%);
  padding: 0;
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 100;
  box-shadow: 4px 0 10px rgba(0, 0, 0, 0.1);

  @media (max-width: 1024px) {
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    
    &.open {
      transform: translateX(0);
    }
  }
`;

export const SidebarHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

export const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: white;

  svg {
    font-size: 2rem;
    color: ${props => props.theme.colors.secondary};
  }

  span {
    font-size: 1.25rem;
    font-weight: 700;
    letter-spacing: -0.025em;
  }
`;

export const NavSection = styled.div`
  padding: 1rem 0;
  flex: 1;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
  }
`;

export const NavLabel = styled.div`
  padding: 0.5rem 1.5rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 0.5rem;
`;

export const NavItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.875rem 1.5rem;
  border: none;
  background: ${props => props.$active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  color: ${props => props.$active ? '#fff' : 'rgba(255, 255, 255, 0.7)'};
  border-left: 3px solid ${props => props.$active ? props.theme.colors.secondary : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  font-weight: ${props => props.$active ? '600' : '500'};
  text-align: left;
  text-decoration: none;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }

  svg {
    font-size: 1.1rem;
    width: 20px;
    color: ${props => props.$active ? props.theme.colors.secondary : 'rgba(255, 255, 255, 0.5)'};
  }
`;

export const LogoutButton = styled(NavItem)`
  margin-top: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: #f87171;

  svg {
    color: #f87171;
  }

  &:hover {
    background: rgba(248, 113, 113, 0.1);
    color: #fca5a5;
  }
`;

export const MainContent = styled.main`
  flex: 1;
  margin-left: 280px;
  min-height: 100vh;

  @media (max-width: 1024px) {
    margin-left: 0;
  }
`;

export const TopBar = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 50;
`;

export const PageTitle = styled.div`
  h1 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
  }

  p {
    font-size: 0.875rem;
    color: #64748b;
    margin: 0.25rem 0 0;
  }
`;

export const TopBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

export const IconButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: ${props => props.$active ? '#f1f5f9' : 'transparent'};
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
  }
`;

export const NotificationDot = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 8px;
  height: 8px;
  background: #ef4444;
  border-radius: 50%;
  border: 2px solid white;
`;

export const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, #8b5cf6 100%);
  color: white;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
`;

export const ContentArea = styled.div`
  padding: 2rem;
`;

// ==========================================
// Card Components
// ==========================================

export const Card = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
`;

export const CardTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

export const CardBody = styled.div`
  padding: 1.5rem;
`;

export const CardFooter = styled.div`
  padding: 1rem 1.5rem;
  background: #f8fafc;
  border-top: 1px solid #f1f5f9;
`;

// ==========================================
// Stats Components
// ==========================================

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

export const StatCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

export const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  background: ${props => props.$color || props.theme.colors.primary}15;
  color: ${props => props.$color || props.theme.colors.primary};
`;

export const StatContent = styled.div`
  flex: 1;
`;

export const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
`;

export const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #64748b;
  margin-top: 0.25rem;
`;

export const StatChange = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${props => props.$positive ? '#10b981' : '#ef4444'};
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.5rem;
`;

// ==========================================
// Grid & List Components
// ==========================================

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${props => props.$cols || 2}, 1fr);
  gap: ${props => props.$gap || '1.5rem'};

  @media (max-width: 1024px) {
    grid-template-columns: ${props => props.$tabletCols ? `repeat(${props.$tabletCols}, 1fr)` : '1fr'};
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const List = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
`;

// ==========================================
// Form Components
// ==========================================

export const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

export const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
`;

export const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}15;
  }

  &::placeholder {
    color: #9ca3af;
  }

  &:disabled {
    background: #f9fafb;
    cursor: not-allowed;
  }
`;

export const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 0.95rem;
  resize: vertical;
  min-height: 120px;
  transition: all 0.2s ease;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}15;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

export const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 0.95rem;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}15;
  }
`;

// ==========================================
// Button Components
// ==========================================

export const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: ${props => props.$size === 'sm' ? '0.5rem 1rem' : props.$size === 'lg' ? '1rem 2rem' : '0.75rem 1.5rem'};
  font-size: ${props => props.$size === 'sm' ? '0.875rem' : props.$size === 'lg' ? '1rem' : '0.95rem'};
  font-weight: 600;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => {
    switch (props.$variant) {
      case 'secondary':
        return `
          background: #f1f5f9;
          color: #475569;
          &:hover:not(:disabled) {
            background: #e2e8f0;
          }
        `;
      case 'success':
        return `
          background: #10b981;
          color: white;
          &:hover:not(:disabled) {
            background: #059669;
          }
        `;
      case 'danger':
        return `
          background: #ef4444;
          color: white;
          &:hover:not(:disabled) {
            background: #dc2626;
          }
        `;
      case 'ghost':
        return `
          background: transparent;
          color: #64748b;
          &:hover:not(:disabled) {
            background: #f1f5f9;
            color: #0f172a;
          }
        `;
      case 'outline':
        return `
          background: transparent;
          color: ${props.theme.colors.primary};
          border: 2px solid ${props.theme.colors.primary};
          &:hover:not(:disabled) {
            background: ${props.theme.colors.primary}10;
          }
        `;
      default:
        return `
          background: linear-gradient(135deg, ${props.theme.colors.primary} 0%, #8b5cf6 100%);
          color: white;
          box-shadow: 0 2px 8px ${props.theme.colors.primary}40;
          &:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px ${props.theme.colors.primary}50;
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

// ==========================================
// Alert Components
// ==========================================

export const Alert = styled.div`
  padding: 1rem 1.25rem;
  border-radius: 10px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.9rem;
  font-weight: 500;

  ${props => {
    switch (props.$type) {
      case 'error':
        return `
          background: #fef2f2;
          color: #dc2626;
          border: 1px solid #fecaca;
        `;
      case 'success':
        return `
          background: #f0fdf4;
          color: #16a34a;
          border: 1px solid #bbf7d0;
        `;
      case 'warning':
        return `
          background: #fffbeb;
          color: #d97706;
          border: 1px solid #fde68a;
        `;
      default:
        return `
          background: #eff6ff;
          color: #2563eb;
          border: 1px solid #bfdbfe;
        `;
    }
  }}

  svg {
    font-size: 1.1rem;
    flex-shrink: 0;
  }
`;

// ==========================================
// Quick Action Components
// ==========================================

export const QuickActionCard = styled(motion.button)`
  background: white;
  border: 2px solid #f1f5f9;
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  width: 100%;

  &:hover {
    border-color: ${props => props.$color || props.theme.colors.primary};
    box-shadow: 0 4px 12px ${props => props.$color || props.theme.colors.primary}15;
    transform: translateY(-2px);
  }
`;

export const QuickActionIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  background: ${props => props.$color || props.theme.colors.primary}12;
  color: ${props => props.$color || props.theme.colors.primary};
`;

export const QuickActionLabel = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  text-align: center;
`;

// ==========================================
// Section Components
// ==========================================

export const Section = styled.section`
  margin-bottom: 2rem;
`;

export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
`;

export const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const SectionLink = styled.button`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.theme.colors.primary};
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

// ==========================================
// Empty State
// ==========================================

export const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  background: #f8fafc;
  border-radius: 16px;
  border: 2px dashed #e2e8f0;
`;

export const EmptyStateIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

export const EmptyStateTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.5rem;
`;

export const EmptyStateText = styled.p`
  font-size: 0.9rem;
  color: #6b7280;
  margin: 0 0 1.5rem;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

// ==========================================
// Badge Components
// ==========================================

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 9999px;
  
  ${props => {
    switch (props.$variant) {
      case 'success':
        return `
          background: #dcfce7;
          color: #166534;
        `;
      case 'warning':
        return `
          background: #fef3c7;
          color: #92400e;
        `;
      case 'danger':
        return `
          background: #fee2e2;
          color: #dc2626;
        `;
      case 'info':
        return `
          background: #e0f2fe;
          color: #0369a1;
        `;
      default:
        return `
          background: #f1f5f9;
          color: #475569;
        `;
    }
  }}
`;

// ==========================================
// Table Components
// ==========================================

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const TableHead = styled.thead`
  background: #f8fafc;
`;

export const TableRow = styled.tr`
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fafafa;
  }
`;

export const TableHeader = styled.th`
  text-align: left;
  padding: 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
`;

export const TableCell = styled.td`
  padding: 1rem;
  font-size: 0.9rem;
  color: #374151;
  vertical-align: middle;
`;

// ==========================================
// Tab Components
// ==========================================

export const TabList = styled.div`
  display: flex;
  gap: 0.25rem;
  background: #f1f5f9;
  padding: 0.25rem;
  border-radius: 10px;
  margin-bottom: 1.5rem;
`;

export const Tab = styled.button`
  flex: 1;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$active ? '#0f172a' : '#64748b'};
  box-shadow: ${props => props.$active ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'};

  &:hover {
    color: #0f172a;
  }
`;

// ==========================================
// Activity Item
// ==========================================

export const ActivityItem = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
`;

export const ActivityIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  background: ${props => props.$color || '#f1f5f9'};
  color: ${props => props.$textColor || '#64748b'};
  flex-shrink: 0;
`;

export const ActivityContent = styled.div`
  flex: 1;
`;

export const ActivityTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  color: #0f172a;
`;

export const ActivityDescription = styled.div`
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 0.25rem;
`;

export const ActivityTime = styled.div`
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 0.25rem;
`;

// ==========================================
// Status Indicator
// ==========================================

export const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => {
    switch (props.$status) {
      case 'active':
      case 'online':
      case 'success':
        return '#10b981';
      case 'warning':
      case 'pending':
        return '#f59e0b';
      case 'error':
      case 'offline':
        return '#ef4444';
      default:
        return '#94a3b8';
    }
  }};
`;
